// Commercial Breaks Manager — admin library for short sponsor clips.
//
// Scope (this milestone): library / upload / metadata edit / activate-deactivate
// / delete / preview. The actual scheduling-into-queue logic is wired in a
// follow-up milestone once cadence (per-station vs global vs per-listener) is
// confirmed.
//
// Constraints enforced client-side AND server-side:
//   - MP3 or MP4 only
//   - <= 5 MB on disk
//   - <= 15 seconds duration (validated via HTMLMediaElement.duration before POST)

import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError, BACKEND_URL } from "../lib/api";
import {
  Megaphone, Upload, Trash2, Edit3, Save, Play, Pause, RefreshCw,
  ToggleRight, ToggleLeft, Link as LinkIcon, X, AlertCircle, Check, FileMusic,
} from "lucide-react";
import TagChipsInput from "./TagChipsInput";
import { STATIONS } from "../lib/stations";

const MAX_BYTES = 5 * 1024 * 1024;
const MAX_DURATION = 15.0;

function formatDuration(s) {
  if (!Number.isFinite(s) || s <= 0) return "—";
  return `${s.toFixed(1)}s`;
}

function formatBytes(b) {
  if (!Number.isFinite(b) || b <= 0) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

// Read duration from a File via a hidden HTMLMediaElement.
function readDuration(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const el = file.type.startsWith("video/")
      ? document.createElement("video")
      : document.createElement("audio");
    el.preload = "metadata";
    el.muted = true;
    el.onloadedmetadata = () => {
      const d = el.duration;
      URL.revokeObjectURL(url);
      if (!Number.isFinite(d)) reject(new Error("Could not read duration"));
      else resolve(d);
    };
    el.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not read media metadata"));
    };
    el.src = url;
  });
}

function CommercialUploadForm({ onUploaded }) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [duration, setDuration] = useState(0);
  const [sponsor, setSponsor] = useState("");
  const [cta, setCta] = useState("");
  const [url, setUrl] = useState("");
  const [stations, setStations] = useState([]);
  const [active, setActive] = useState(true);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");

  const onPick = async (e) => {
    setErr(""); setOk("");
    const f = e.target.files?.[0];
    if (!f) { setFile(null); setDuration(0); return; }
    const ext = (f.name.split(".").pop() || "").toLowerCase();
    if (!["mp3", "mp4"].includes(ext)) {
      setErr("Commercial must be MP3 or MP4."); e.target.value = ""; return;
    }
    if (f.size > MAX_BYTES) {
      setErr(`File is ${formatBytes(f.size)} — max ${formatBytes(MAX_BYTES)}.`); e.target.value = ""; return;
    }
    try {
      const d = await readDuration(f);
      if (d > MAX_DURATION + 0.5) {
        setErr(`Clip is ${d.toFixed(1)}s — max ${MAX_DURATION}s.`);
        e.target.value = ""; return;
      }
      setFile(f); setDuration(d);
    } catch (er) {
      setErr(er.message || "Could not read file");
      e.target.value = "";
    }
  };

  const reset = () => {
    setFile(null); setDuration(0); setSponsor(""); setCta(""); setUrl("");
    setStations([]); setActive(true); setNotes("");
    if (fileRef.current) fileRef.current.value = "";
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    setErr(""); setOk("");
    if (!file) { setErr("Pick an MP3 or MP4 file first."); return; }
    if (!sponsor.trim()) { setErr("Sponsor name is required."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("audio", file);
      fd.append("sponsor", sponsor.trim());
      if (cta.trim()) fd.append("cta", cta.trim());
      if (url.trim()) fd.append("url", url.trim());
      if (stations.length) fd.append("stations", stations.join(","));
      fd.append("active", String(active));
      if (notes.trim()) fd.append("notes", notes.trim());
      fd.append("duration_seconds", String(duration));
      await api.post("/admin/commercials", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setOk("Commercial uploaded."); reset(); onUploaded?.();
    } catch (er) {
      setErr(formatApiError(er));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="card-surface p-4 md:p-6 space-y-3" data-testid="commercial-upload-form">
      <div className="flex items-center gap-2">
        <Megaphone className="w-5 h-5 text-[#C5A059]" strokeWidth={1.5} />
        <h3 className="font-serif text-xl text-[#FDFBF7]">Upload commercial break</h3>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        <div className="md:col-span-2">
          <label className="overline text-xs text-[#A1A4AB] block mb-1">MP3 or MP4 · ≤ {MAX_DURATION}s · ≤ {formatBytes(MAX_BYTES)}</label>
          <input
            ref={fileRef}
            type="file"
            accept=".mp3,.mp4,audio/mpeg,video/mp4"
            onChange={onPick}
            className="input-lux"
            data-testid="commercial-file-input"
          />
          {file && (
            <div className="text-xs text-[#A1A4AB] mt-1.5 flex items-center gap-3">
              <FileMusic className="w-3.5 h-3.5 text-[#C5A059]" strokeWidth={1.5} />
              <span>{file.name}</span>
              <span>·</span>
              <span>{formatDuration(duration)}</span>
              <span>·</span>
              <span>{formatBytes(file.size)}</span>
            </div>
          )}
        </div>
        <input
          required className="input-lux" placeholder="Sponsor name *"
          value={sponsor} onChange={(e) => setSponsor(e.target.value)}
          data-testid="commercial-sponsor"
        />
        <input
          className="input-lux" placeholder='CTA (e.g. "Visit us today")'
          value={cta} onChange={(e) => setCta(e.target.value)}
          data-testid="commercial-cta"
        />
        <input
          className="input-lux md:col-span-2" placeholder="URL (https://…)"
          value={url} onChange={(e) => setUrl(e.target.value)}
          data-testid="commercial-url"
        />
      </div>

      <TagChipsInput
        label="Station targeting (leave empty = all stations)"
        value={stations}
        onChange={setStations}
        suggestions={STATIONS.map((s) => s.name)}
        placeholder="Add station — Enter to confirm"
        testId="commercial-stations"
        maxItems={20}
      />

      <textarea
        className="input-lux" placeholder="Internal notes (optional)"
        value={notes} onChange={(e) => setNotes(e.target.value)}
        rows={2} data-testid="commercial-notes"
      />

      <div className="flex items-center justify-between flex-wrap gap-2 pt-1">
        <label className="inline-flex items-center gap-2 text-sm text-[#A1A4AB] cursor-pointer">
          <input
            type="checkbox" checked={active}
            onChange={(e) => setActive(e.target.checked)}
            data-testid="commercial-active"
          />
          Active immediately
        </label>
        <div className="flex items-center gap-2">
          {ok && <span className="text-xs text-emerald-400 inline-flex items-center gap-1"><Check className="w-3 h-3" strokeWidth={2} /> {ok}</span>}
          {err && <span className="text-xs text-red-400 inline-flex items-center gap-1"><AlertCircle className="w-3 h-3" strokeWidth={2} /> {err}</span>}
          <button type="submit" disabled={busy || !file} className="btn-primary disabled:opacity-40" data-testid="commercial-submit">
            <Upload className="w-4 h-4" strokeWidth={1.5} /> {busy ? "Uploading…" : "Upload"}
          </button>
        </div>
      </div>
    </form>
  );
}

function CommercialPreview({ commercialId, mediaType }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [err, setErr] = useState(false);
  const [playing, setPlaying] = useState(false);
  const elRef = useRef(null);

  useEffect(() => {
    let revoked = false; let createdUrl = null;
    (async () => {
      try {
        const resp = await api.get(`/admin/commercials/${commercialId}/stream`, { responseType: "blob" });
        if (revoked) return;
        const ct = resp.data?.type || resp.headers?.["content-type"] || "";
        const blob = ct ? new Blob([resp.data], { type: ct }) : resp.data;
        createdUrl = URL.createObjectURL(blob);
        setBlobUrl(createdUrl);
      } catch {
        if (!revoked) setErr(true);
      }
    })();
    return () => { revoked = true; if (createdUrl) URL.revokeObjectURL(createdUrl); };
  }, [commercialId]);

  const toggle = () => {
    const el = elRef.current; if (!el) return;
    if (el.paused) { el.play().catch(() => {}); setPlaying(true); }
    else { el.pause(); setPlaying(false); }
  };
  const onEnd = () => setPlaying(false);

  if (err) return <div className="text-xs text-red-400">Preview failed</div>;
  if (!blobUrl) return <div className="text-xs text-[#A1A4AB]">Loading preview…</div>;

  return (
    <div className="flex items-center gap-2">
      {mediaType === "video" ? (
        <video ref={elRef} src={blobUrl} onEnded={onEnd} className="w-20 h-12 rounded border border-[#22262E] bg-black object-cover" muted playsInline />
      ) : (
        <audio ref={elRef} src={blobUrl} onEnded={onEnd} />
      )}
      <button
        onClick={toggle}
        className="w-8 h-8 rounded-full border border-[#C5A059]/40 text-[#C5A059] hover:bg-[#C5A059]/10 flex items-center justify-center"
        aria-label={playing ? "Pause" : "Play"}
        data-testid={`commercial-preview-toggle-${commercialId}`}
      >
        {playing ? <Pause className="w-3.5 h-3.5" strokeWidth={2} /> : <Play className="w-3.5 h-3.5 ml-0.5" strokeWidth={2} />}
      </button>
    </div>
  );
}

function CommercialRow({ item, onChanged, onDeleted }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({
    sponsor: item.sponsor || "",
    cta: item.cta || "",
    url: item.url || "",
    stations: item.stations || [],
    notes: item.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const toggleActive = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.patch(`/admin/commercials/${item.id}`, { active: !item.active });
      onChanged?.(data);
    } catch (e) { setErr(formatApiError(e)); } finally { setBusy(false); }
  };

  const save = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.patch(`/admin/commercials/${item.id}`, {
        sponsor: draft.sponsor.trim() || null,
        cta: draft.cta.trim() || null,
        url: draft.url.trim() || null,
        stations: draft.stations,
        notes: draft.notes.trim() || null,
      });
      onChanged?.(data);
      setEditing(false);
    } catch (e) { setErr(formatApiError(e)); } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete commercial for "${item.sponsor}"? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/commercials/${item.id}`);
      onDeleted?.(item.id);
    } catch (e) { setErr(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className={`card-surface p-3 md:p-4 ${item.active ? "" : "opacity-60"}`} data-testid={`commercial-row-${item.id}`}>
      <div className="grid md:grid-cols-[140px_1fr_auto] gap-3 md:gap-4 items-center">
        <CommercialPreview commercialId={item.id} mediaType={item.media_type} />
        <div className="min-w-0">
          {editing ? (
            <div className="grid md:grid-cols-2 gap-2">
              <input className="input-lux !py-1.5 text-sm" placeholder="Sponsor" value={draft.sponsor} onChange={(e) => setDraft({ ...draft, sponsor: e.target.value })} data-testid={`commercial-edit-sponsor-${item.id}`} />
              <input className="input-lux !py-1.5 text-sm" placeholder="CTA" value={draft.cta} onChange={(e) => setDraft({ ...draft, cta: e.target.value })} />
              <input className="input-lux !py-1.5 text-sm md:col-span-2" placeholder="URL" value={draft.url} onChange={(e) => setDraft({ ...draft, url: e.target.value })} />
              <div className="md:col-span-2">
                <TagChipsInput
                  label="Station targeting"
                  value={draft.stations}
                  onChange={(v) => setDraft({ ...draft, stations: v })}
                  suggestions={STATIONS.map((s) => s.name)}
                  placeholder="All stations if empty"
                  testId={`commercial-edit-stations-${item.id}`}
                />
              </div>
              <textarea className="input-lux !py-1.5 text-sm md:col-span-2" rows={2} placeholder="Notes" value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} />
            </div>
          ) : (
            <>
              <div className="text-[#FDFBF7] font-serif text-base flex items-center gap-2">
                {item.sponsor || "(no sponsor)"}
                {item.active ? (
                  <span className="overline text-[10px] text-emerald-400 px-1.5 py-0.5 rounded-full bg-emerald-400/10 border border-emerald-400/30">ACTIVE</span>
                ) : (
                  <span className="overline text-[10px] text-[#A1A4AB] px-1.5 py-0.5 rounded-full bg-[#22262E]/40 border border-[#22262E]">PAUSED</span>
                )}
              </div>
              {item.cta && <div className="text-sm text-[#A1A4AB] truncate">{item.cta}</div>}
              {item.url && (
                <a href={item.url} target="_blank" rel="noreferrer" className="text-xs text-[#C5A059] inline-flex items-center gap-1 mt-0.5 hover:underline">
                  <LinkIcon className="w-3 h-3" strokeWidth={1.5} /> {item.url}
                </a>
              )}
              <div className="text-xs text-[#6C707A] mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                <span>{formatDuration(item.duration_seconds)}</span>
                <span>{formatBytes(item.size_bytes)}</span>
                <span className="uppercase">{item.media_type === "video" ? "MP4" : "MP3"}</span>
                <span>
                  {item.stations && item.stations.length > 0
                    ? `Targets: ${item.stations.join(", ")}`
                    : "All stations"}
                </span>
              </div>
              {item.notes && <div className="text-xs text-[#6C707A] italic mt-1 truncate">Note: {item.notes}</div>}
            </>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 flex-wrap justify-end">
          {editing ? (
            <>
              <button onClick={save} disabled={busy} className="btn-primary !px-3 !py-1.5 text-sm disabled:opacity-40" data-testid={`commercial-save-${item.id}`}>
                <Save className="w-3.5 h-3.5" strokeWidth={1.5} /> Save
              </button>
              <button onClick={() => setEditing(false)} disabled={busy} className="btn-ghost !px-3 !py-1.5 text-sm">
                <X className="w-3.5 h-3.5" strokeWidth={1.5} /> Cancel
              </button>
            </>
          ) : (
            <>
              <button
                onClick={toggleActive}
                disabled={busy}
                title={item.active ? "Deactivate" : "Activate"}
                className="btn-ghost !px-3 !py-1.5 text-sm"
                data-testid={`commercial-toggle-${item.id}`}
              >
                {item.active ? <ToggleRight className="w-4 h-4 text-emerald-400" strokeWidth={1.5} /> : <ToggleLeft className="w-4 h-4 text-[#A1A4AB]" strokeWidth={1.5} />}
              </button>
              <button onClick={() => setEditing(true)} className="btn-ghost !px-3 !py-1.5 text-sm">
                <Edit3 className="w-3.5 h-3.5" strokeWidth={1.5} />
              </button>
              <button onClick={remove} disabled={busy} className="btn-ghost !px-3 !py-1.5 text-sm text-red-400 hover:!border-red-400/40" data-testid={`commercial-delete-${item.id}`}>
                <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
              </button>
            </>
          )}
        </div>
      </div>
      {err && <div className="text-xs text-red-400 mt-2">{err}</div>}
    </div>
  );
}

export default function CommercialBreaksManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const { data } = await api.get("/admin/commercials");
      setItems(data || []);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const stats = useMemo(() => {
    const active = items.filter((i) => i.active).length;
    return { total: items.length, active, paused: items.length - active };
  }, [items]);

  return (
    <section className="space-y-4" data-testid="commercial-breaks-section">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="overline text-[#C5A059] mb-1">Sponsor library</div>
          <h2 className="font-serif text-2xl text-[#FDFBF7] tracking-tight flex items-center gap-2">
            <Megaphone className="w-5 h-5 text-[#C5A059]" strokeWidth={1.5} />
            Commercial breaks
          </h2>
          <p className="text-xs text-[#A1A4AB] mt-1">
            Active: <span className="text-emerald-400">{stats.active}</span>
            <span className="mx-2 text-[#22262E]">|</span>
            Paused: <span className="text-[#A1A4AB]">{stats.paused}</span>
            <span className="mx-2 text-[#22262E]">|</span>
            Total: <span className="text-[#FDFBF7]">{stats.total}</span>
          </p>
        </div>
        <button onClick={load} className="btn-ghost text-sm" data-testid="commercials-refresh">
          <RefreshCw className="w-4 h-4 inline mr-1" strokeWidth={1.5} /> Refresh
        </button>
      </div>

      <CommercialUploadForm onUploaded={load} />

      {err && <div className="text-red-400 text-sm">{err}</div>}

      {loading ? (
        <div className="space-y-2">{Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-20 rounded-md shimmer" />)}</div>
      ) : items.length === 0 ? (
        <div className="card-surface p-10 text-center" data-testid="commercials-empty">
          <Megaphone className="w-8 h-8 text-[#C5A059]/60 mx-auto mb-3" strokeWidth={1.5} />
          <div className="text-[#FDFBF7] font-serif text-xl">No commercials yet</div>
          <div className="text-[#A1A4AB] text-sm mt-1">Upload a 15-second clip above to start building your sponsor library.</div>
        </div>
      ) : (
        <div className="space-y-2" data-testid="commercials-list">
          {items.map((item) => (
            <CommercialRow
              key={item.id}
              item={item}
              onChanged={(updated) => setItems((prev) => prev.map((i) => (i.id === updated.id ? updated : i)))}
              onDeleted={(id) => setItems((prev) => prev.filter((i) => i.id !== id))}
            />
          ))}
        </div>
      )}
    </section>
  );
}
